import { IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonContent, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonNote, IonPage, IonReorder, IonReorderGroup, IonSegment, IonSegmentButton, IonText, IonTitle, IonToggle, IonToolbar, useIonModal } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { type ReactElement, useEffect, useState } from 'react';
import { useDeviceTransitPolicyMutation, useDeviceTransitPolicyQuery } from '../query/deviceTransitPolicyQuery';
import { JsonEditor } from 'json-edit-react';
import { TransitPolicy, TransitPolicyRule } from '../models/TransitPolicy';
import { addOutline, list, codeSlash, chevronForward, createOutline, closeOutline, checkmarkOutline } from 'ionicons/icons';
import { EventClassification, EventTriggerSource } from '../models/DeviceEvent';
import { FlapState, MotionSensorState } from '../models/FrameMetadata';
import RfidChip from '../components/RfidChip';
import styles from './DoorPolicyModal.module.css';
import { useDeveloperModeQuery } from '../query/developerModeQuery';
import { Browser } from '@capacitor/browser';
import { CapacitorCrisp } from '@capgo/capacitor-crisp';
import { modalController } from '@ionic/core';
import type { ItemReorderEventDetail } from '@ionic/core';
import EditDoorPolicyRule from './EditDoorPolicyRule';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { formatDurationLabel, getClassificationLabel, getFlapStateLabel, getMotionSensorStateLabel, getTriggerSourceLabel } from '../utils/doorPolicyTranslations';
import { EditDoorPolicySoundPickerView, getSoundLabel } from './edit-door-policy/EditDoorPolicySoundPicker';
import { Trans } from 'react-i18next';

enum Tabs {
    Rules,
    Code
}

const isRecord = (value: unknown): value is Record<string, any> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeTransitPolicy = (policy: unknown): TransitPolicy | undefined => {
    if (!isRecord(policy)) {
        return undefined;
    }

    const normalizedPolicy = new TransitPolicy(policy);
    const rawRules = Array.isArray(policy.rules) ? policy.rules : [];
    normalizedPolicy.rules = rawRules.map((rule) =>
        rule instanceof TransitPolicyRule ? rule : new TransitPolicyRule(isRecord(rule) ? rule : {})
    );

    return normalizedPolicy;
};

const DoorPolicyModal = ({ dismiss, deviceTransitPolicyId }: { dismiss: (data?: string | null | undefined | number, role?: string) => void, deviceTransitPolicyId: number }) => {
    const [edited, setEdited] = useState(false);
    const query = useDeviceTransitPolicyQuery(deviceTransitPolicyId);
    const [name, setName] = useState('');
    const [transitPolicy, setTransitPolicy] = useState<TransitPolicy>();
    const [tab, setTab] = useState<Tabs>(Tabs.Rules);
    const [isReorderMode, setIsReorderMode] = useState(false);
    const mutation = useDeviceTransitPolicyMutation(deviceTransitPolicyId);
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;
    const [ruleModalState, setRuleModalState] = useState<{ mode: 'edit' | 'add'; index: number } | null>(null);
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const policyRules = Array.isArray(transitPolicy?.rules) ? transitPolicy.rules : [];
    const selectedRule =
        ruleModalState?.mode === 'edit' ? policyRules[ruleModalState.index] : undefined;
    const ruleForModal =
        ruleModalState?.mode === 'add' ? () => new TransitPolicyRule({}) : selectedRule;
    const [presentEditRuleModal, dismissEditRuleModal] = useIonModal(EditDoorPolicyRule, {
        dismiss: () => {
            dismissEditRuleModal();
            setRuleModalState(null);
        },
        onDelete: (index?: number) => {
            if (index === undefined) {
                return;
            }
            setTransitPolicy((prevPolicy) => {
                if (!prevPolicy) {
                    return prevPolicy;
                }
                const rules = Array.isArray(prevPolicy.rules) ? [...prevPolicy.rules] : [];
                rules.splice(index, 1);
                return new TransitPolicy({
                    ...prevPolicy,
                    rules,
                });
            });
            setEdited(true);
        },
        deviceId: query.data?.deviceId ?? '',
        onSave: (updatedRule: TransitPolicyRule, index?: number) => {
            if (index === undefined) {
                return;
            }
            setTransitPolicy((prevPolicy) => {
                if (!prevPolicy) {
                    return prevPolicy;
                }
                const rules = Array.isArray(prevPolicy.rules) ? [...prevPolicy.rules] : [];
                if (ruleModalState?.mode === 'add') {
                    rules.splice(index, 0, updatedRule);
                } else {
                    rules[index] = updatedRule;
                }
                return new TransitPolicy({
                    ...prevPolicy,
                    rules,
                });
            });
            setEdited(true);
        },
        rule: ruleForModal,
        ruleIndex: ruleModalState?.index,
    });
    const activationSound = transitPolicy?.ux?.onActivate?.sound ?? '';
    const activationSoundLabel = getSoundLabel(tModals, activationSound);
    const [presentSoundPickerModal, dismissSoundPickerModal] = useIonModal(EditDoorPolicySoundPickerView, {
        soundInput: activationSound,
        onSelectSound: (soundId: string) => {
            setTransitPolicy((prevPolicy) => {
                if (!prevPolicy) {
                    return prevPolicy;
                }
                return new TransitPolicy({
                    ...prevPolicy,
                    ux: {
                        ...(prevPolicy.ux ?? {}),
                        onActivate: {
                            ...(prevPolicy.ux?.onActivate ?? {}),
                            sound: soundId,
                        },
                    },
                });
            });
            setEdited(true);
        },
        onClose: () => {
            dismissSoundPickerModal();
        },
    });

    useEffect(() => {
        setName(query.data?.name || '');
        setTransitPolicy(normalizeTransitPolicy(query.data?.transitPolicy));
        setEdited(false);
    }, [query.data?.transitPolicy]);

    useEffect(() => {
        if (!ruleModalState) {
            return;
        }
        if (!query.data?.deviceId) {
            return;
        }

        const showModal = async () => {
            const presentingElement = await modalController.getTop();
            await presentEditRuleModal({
                presentingElement: presentingElement ?? undefined,
            });
        };

        showModal();
    }, [presentEditRuleModal, query.data?.deviceId, ruleModalState]);

    const save = async () => {
        await mutation.mutateAsync({ transitPolicy, name });

        dismiss(undefined, 'confirm');
    }

    const handleReorder = (event: CustomEvent<ItemReorderEventDetail>) => {
        if (!transitPolicy) {
            event.detail.complete();
            return;
        }

        const nextRules = event.detail.complete([...policyRules]);
        setTransitPolicy(new TransitPolicy({
            ...transitPolicy,
            rules: nextRules,
        }));
        setEdited(true);
    };

    const openEditRuleModal = (ruleIndex: number) => {
        setRuleModalState({ mode: 'edit', index: ruleIndex });
    };

    const openAddRuleModal = (insertIndex: number) => {
        setRuleModalState({ mode: 'add', index: insertIndex });
    };

    const openCrispChat = async () => {
        await CapacitorCrisp.openMessenger();
    };

    const idleLockEnabled = transitPolicy?.idleLock ?? true;
    const idleLockBatteryEnabled = transitPolicy?.idleLockBattery ?? idleLockEnabled;
    const unlockOnPowerLoss = !idleLockBatteryEnabled;

    const setIdleLock = (idleLock: boolean) => {
        setTransitPolicy((prevPolicy) => {
            if (!prevPolicy) {
                return prevPolicy;
            }
            return new TransitPolicy({
                ...prevPolicy,
                idleLock,
            });
        });
        setEdited(true);
    };

    const setIdleLockBattery = (unlockOnPowerLoss: boolean) => {
        setTransitPolicy((prevPolicy) => {
            if (!prevPolicy) {
                return prevPolicy;
            }
            return new TransitPolicy({
                ...prevPolicy,
                idleLockBattery: !unlockOnPowerLoss,
            });
        });
        setEdited(true);
    };

    const openActivationSoundPicker = async () => {
        const presentingElement = await modalController.getTop();
        await presentSoundPickerModal({
            presentingElement: presentingElement ?? undefined,
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton
                            color="medium"
                            onClick={() => dismiss(null, 'cancel')}
                            aria-label={tCommon('buttons.cancel')}
                        >
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                    <IonTitle>
                        <IonInput
                            aria-label={tModals('door_policy.name_label')}
                            mode="ios"
                            value={name}
                            placeholder={tModals('door_policy.name_placeholder')}
                            onIonInput={(e) => {
                                if (transitPolicy) {
                                    setName(e.detail.value!);
                                    setEdited(true);
                                }
                            }}
                        ></IonInput>
                    </IonTitle>
                    <IonButtons slot="end">
                        {!isReorderMode && edited && (
                            <IonButton
                                onClick={save}
                                strong={true}
                                disabled={mutation.isPending}
                                aria-label={tCommon('buttons.save')}
                            >
                                <IonIcon slot="icon-only" icon={checkmarkOutline} />
                            </IonButton>
                        )}
                    </IonButtons>
                </IonToolbar>
                {developerMode && (
                    <IonToolbar>
                        <IonSegment value={tab} onIonChange={e => setTab(e.detail.value as Tabs)}>
                            <IonSegmentButton layout="icon-start" value={Tabs.Rules}>
                                <IonIcon icon={list} />
                                <IonLabel>{tModals('door_policy.tabs.rules')}</IonLabel>
                            </IonSegmentButton>
                            <IonSegmentButton layout="icon-start" value={Tabs.Code}>
                                <IonIcon icon={codeSlash} />
                                <IonLabel>{tModals('door_policy.tabs.code')}</IonLabel>
                            </IonSegmentButton>
                        </IonSegment>
                    </IonToolbar>
                )}
            </IonHeader>
            <IonContent color="light" fullscreen className={styles.content}>
                {tab === Tabs.Rules && (
                    <div className={styles.wrapper}>
                        <section className={styles.section}>
                            <div className={styles.sectionTitleRow}>
                                <h2 className={styles.sectionTitle}>{tModals('door_policy.sections.rules')}</h2>
                                <IonButton
                                    color="medium"
                                    fill="clear"
                                    onClick={() => setIsReorderMode((prev) => !prev)}
                                    aria-label={
                                        isReorderMode
                                            ? tModals('door_policy.buttons.done')
                                            : tModals('door_policy.buttons.edit')
                                    }
                                >
                                    <IonIcon
                                        slot="icon-only"
                                        icon={isReorderMode ? checkmarkOutline : createOutline}
                                    />
                                </IonButton>
                            </div>
                            <IonReorderGroup
                                disabled={!isReorderMode}
                                onIonItemReorder={handleReorder}
                                className={styles.ruleList}
                            >
                                {policyRules.map((rule, idx) => (
                                    <IonItem
                                        key={rule.editorKey ?? rule.description ?? idx}
                                        lines="none"
                                        detail={false}
                                        className={styles.ruleItem}
                                        onClick={() => {
                                            openEditRuleModal(idx);
                                        }}
                                    >
                                        <div className={`${styles.ruleCardBase} ${styles.ruleCardGrid} ${styles.ruleCardButton}`}>
                                            <div className={styles.ruleColumn}>
                                                <div className={`${styles.cardLabel} ${styles.whenLabel}`}>{tModals('door_policy.sections.when')}</div>
                                                <div className={styles.ruleText}>
                                                    {!rule?.criteria && <span>{tModals('door_policy.rule_summary.always')}</span>}
                                                    {rule?.criteria && Object.entries(rule?.criteria).map(([key, value], criteriaIdx) => {
                                                        const parts: ReactElement[] = [];
                                                        let partIndex = 0;
                                                        const pushText = (text: string) => {
                                                            parts.push(
                                                                <span key={`${key}-${criteriaIdx}-text-${partIndex++}`}>{text}</span>
                                                            );
                                                        };
                                                        const pushStrong = (text: string) => {
                                                            parts.push(
                                                                <strong key={`${key}-${criteriaIdx}-strong-${partIndex++}`}>{text}</strong>
                                                            );
                                                        };
                                                        const pushNode = (node: ReactElement) => {
                                                            parts.push(
                                                                <span key={`${key}-${criteriaIdx}-node-${partIndex++}`}>{node}</span>
                                                            );
                                                        };

                                                        const orLabel = tModals('door_policy.rule_summary.or');
                                                        pushText(
                                                            `${criteriaIdx === 0 ? tModals('door_policy.rule_summary.if') : tModals('door_policy.rule_summary.and')} `
                                                        );
                                                        value = Array.isArray(value) ? value : [value];

                                                        switch (key) {
                                                            case 'timeRange':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.time_between')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(String(value[i]));
                                                                }
                                                                break;

                                                            case 'eventTriggerSource':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.event_triggered_by')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getTriggerSourceLabel(tModals, Number(value[i]) as EventTriggerSource));
                                                                }
                                                                break;

                                                            case 'eventClassification':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.appears_to_be')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getClassificationLabel(tModals, Number(value[i]) as EventClassification));
                                                                }
                                                                break;

                                                            case 'motionSensorState':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.motion_sensor_is')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getMotionSensorStateLabel(tModals, Number(value[i]) as MotionSensorState));
                                                                }
                                                                break;

                                                            case 'flapState':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.flap_is')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushStrong(getFlapStateLabel(tModals, Number(value[i]) as FlapState));
                                                                }
                                                                break;

                                                            case 'rfidCode':
                                                                for (let i = 0; i < value.length; i++) {
                                                                    pushText(
                                                                        !i
                                                                            ? ` ${tModals('door_policy.rule_summary.read_microchip')} `
                                                                            : ` ${orLabel} `
                                                                    );
                                                                    pushNode(<RfidChip deviceId={query.data?.deviceId} rfidCode={value[i]} />);
                                                                }
                                                                break;

                                                            default:
                                                                pushText(tModals('door_policy.rule_summary.unknown_criteria'));
                                                        }

                                                        return <span key={`${key}-${criteriaIdx}`}>{parts}</span>;
                                                    })}
                                                </div>
                                            </div>
                                            <div className={styles.ruleChevron}>
                                                <IonIcon icon={chevronForward} />
                                            </div>
                                            <div className={styles.ruleColumn}>
                                                <div className={`${styles.cardLabel} ${styles.thenLabel}`}>{tModals('door_policy.sections.then')}</div>
                                                <div className={styles.ruleText}>
                                                    {(() => {
                                                        if (!rule?.action || Object.keys(rule.action).length === 0) {
                                                            return <span>{tModals('door_policy.rule_summary.do_nothing')}</span>;
                                                        }

                                                        const actionItems: Array<{
                                                            key: string;
                                                            render: (pushText: (text: string) => void, pushStrong: (text: string) => void) => void;
                                                        }> = [];
                                                        const action = rule.action;
                                                        const hasLockAction =
                                                            action.lock !== undefined ||
                                                            action.lockoutDuration !== undefined ||
                                                            action.final !== undefined;

                                                        if (hasLockAction) {
                                                            actionItems.push({
                                                                key: 'lock',
                                                                render: (pushText, pushStrong) => {
                                                                    if (action.lock === false) {
                                                                        pushStrong(tModals('door_policy.rule_summary.unlock'));
                                                                        pushText(` ${tModals('door_policy.rule_summary.door_suffix')}`);
                                                                        return;
                                                                    }

                                                                    pushStrong(tModals('door_policy.rule_summary.lock'));
                                                                    pushText(` ${tModals('door_policy.rule_summary.door_suffix')}`);

                                                                    if (action.lockoutDuration !== undefined && Number(action.lockoutDuration) > 0) {
                                                                        const durationLabel = formatDurationLabel(tModals, Number(action.lockoutDuration), { short: true });
                                                                        pushText(` ${tModals('door_policy.rule_summary.for_duration', { duration: durationLabel })}`);
                                                                    } else if (action.final) {
                                                                        pushText(` ${tModals('door_policy.rule_summary.until_event_end')}`);
                                                                    }
                                                                },
                                                            });
                                                        }

                                                        if (action.sound !== undefined) {
                                                            actionItems.push({
                                                                key: 'sound',
                                                                render: (pushText, pushStrong) => {
                                                                    pushText(` ${tModals('door_policy.rule_summary.play_the')} `);
                                                                    pushStrong(getSoundLabel(tModals, String(action.sound)));
                                                                    pushText(` ${tModals('door_policy.rule_summary.sound_suffix')}`);
                                                                },
                                                            });
                                                        }

                                                        Object.keys(action)
                                                            .filter((key) => !['lock', 'lockoutDuration', 'final', 'sound'].includes(key))
                                                            .forEach((key) => {
                                                                actionItems.push({
                                                                    key,
                                                                    render: (pushText) => {
                                                                        pushText(tModals('door_policy.rule_summary.unknown_action'));
                                                                    },
                                                                });
                                                            });

                                                        if (actionItems.length === 0) {
                                                            return <span>{tModals('door_policy.rule_summary.do_nothing')}</span>;
                                                        }

                                                        return actionItems.map((item, actionIdx) => {
                                                            const parts: ReactElement[] = [];
                                                            let partIndex = 0;
                                                            const pushText = (text: string) => {
                                                                parts.push(
                                                                    <span key={`${item.key}-${actionIdx}-text-${partIndex++}`}>{text}</span>
                                                                );
                                                            };
                                                            const pushStrong = (text: string) => {
                                                                parts.push(
                                                                    <strong key={`${item.key}-${actionIdx}-strong-${partIndex++}`}>{text}</strong>
                                                                );
                                                            };

                                                            if (actionIdx !== 0) {
                                                                pushText(`${tModals('door_policy.rule_summary.and')} `);
                                                            }

                                                            item.render(pushText, pushStrong);

                                                            return <span key={`${item.key}-${actionIdx}`}>{parts}</span>;
                                                        });
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        <IonReorder slot="end" className={styles.reorderHandle} />
                                    </IonItem>
                                ))}
                            </IonReorderGroup>
                            {isReorderMode && (
                                <div className={styles.addRuleRow}>
                                    <IonButton
                                        fill="clear"
                                        className={styles.addRuleButton}
                                        onClick={() => {
                                            openAddRuleModal(policyRules.length);
                                        }}
                                    >
                                        <IonIcon icon={addOutline} className={styles.addRuleIcon} />
                                        <span className={styles.addRuleText}>{tModals('door_policy.buttons.add_rule')}</span>
                                    </IonButton>
                                </div>
                            )}
                        </section>
                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>{tModals('door_policy.sections.behavior')}</h2>
                            <IonList className={styles.listCard}>
                                <IonItem>
                                    <IonToggle
                                        checked={!idleLockEnabled}
                                        helperText={tModals('door_policy.behavior.training_mode_helper')}
                                        disabled={!transitPolicy}
                                        onIonChange={(e) => setIdleLock(!e.detail.checked)}
                                    >
                                        <IonLabel>{tModals('door_policy.behavior.training_mode_label')}</IonLabel>
                                    </IonToggle>
                                </IonItem>
                                <IonItem>
                                    <IonToggle
                                        checked={unlockOnPowerLoss}
                                        disabled={!transitPolicy}
                                        onIonChange={(e) => setIdleLockBattery(e.detail.checked)}
                                    >
                                        <IonLabel>{tModals('door_policy.behavior.power_loss_label')}</IonLabel>
                                    </IonToggle>
                                </IonItem>
                                <IonItem
                                    button
                                    onClick={openActivationSoundPicker}
                                    disabled={!transitPolicy}
                                >
                                    <IonLabel>{tModals('door_policy.behavior.activation_sound_label')}</IonLabel>
                                    <IonText slot="end" color="medium">{activationSoundLabel}</IonText>
                                </IonItem>
                            </IonList>
                        </section>
                        <p className={styles.helperText}>
                            <Trans
                                i18nKey="door_policy.support"
                                ns="modals"
                                components={{
                                    supportLink: (
                                        <button type="button" className={styles.supportLink} onClick={openCrispChat} />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}
                {tab === Tabs.Code && (
                    <div className={styles.wrapper}>
                        <section className={styles.section}>
                            <h2 className={styles.sectionTitle}>{tModals('door_policy.sections.code')}</h2>
                            <IonCard color="light">
                                <IonCardHeader>
                                    <IonCardTitle>{tModals('door_policy.code_editor.title')}</IonCardTitle>
                                </IonCardHeader>
                                <IonCardContent>
                                    {tModals('door_policy.code_editor.description')}
                                </IonCardContent>
                                <IonButton fill="clear" onClick={() => Browser.open({ url: "https://www.onlycat.com/door-policy-schema/" })} >
                                    {tModals('door_policy.code_editor.view_schema')}
                                </IonButton>
                            </IonCard>
                            {transitPolicy && <JsonEditor data={transitPolicy}
                                setData={(data) => {
                                    const nextPolicy = normalizeTransitPolicy(data);
                                    if (!nextPolicy) {
                                        return;
                                    }

                                    setTransitPolicy(nextPolicy);
                                    setEdited(true);
                                }}
                                showArrayIndices={false}
                                showCollectionCount="when-closed"
                                maxWidth="100%"
                                rootName="transitPolicy"
                                keySort={(a, b) => {
                                    const aStr = String(a);
                                    const bStr = String(b);

                                    if (aStr === 'description') return -1;
                                    if (bStr === 'description') return 1;
                                    if (aStr === 'criteria') return -1;
                                    if (bStr === 'criteria') return 1;
                                    return aStr.localeCompare(bStr);
                                }}
                            />}
                        </section>
                        <p className={styles.helperText}>
                            <Trans
                                i18nKey="door_policy.support"
                                ns="modals"
                                components={{
                                    supportLink: (
                                        <button type="button" className={styles.supportLink} onClick={openCrispChat} />
                                    ),
                                }}
                            />
                        </p>
                    </div>
                )}
            </IonContent>
        </IonPage >
    );
};

export default DoorPolicyModal;
